import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUserPlan, upgradePlan, downgradePlan, setAuthToken, createPaymentOrder, verifyPayment } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { Helmet } from 'react-helmet-async';

const PricingPage = () => {
  const navigate = useNavigate();
  const [currentPlan, setCurrentPlan] = useState(null);
  const [isTrial, setIsTrial] = useState(false);
  const [trialRemainingHours, setTrialRemainingHours] = useState(0);
  const [trialEnd, setTrialEnd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const { isAuthenticated } = useAuth();

  const fetchPlan = async () => {
    if (!isAuthenticated) {
      setCurrentPlan('none');
      setLoading(false);
      return;
    }
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        setAuthToken(session.access_token);
        console.log("PricingPage: token set before getUserPlan");
      }
      
      const data = await getUserPlan();
      setCurrentPlan(data.plan);
      setIsTrial(data.is_trial);
      setTrialRemainingHours(data.trial_remaining_hours);
      setTrialEnd(data.trial_end);
      setError(null);
    } catch (err) {
      console.error("Failed to fetch plan:", err);
      setCurrentPlan('basic');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlan();
  }, [isAuthenticated]);

  const handleUpgrade = async (plan) => {
    try {
      setActionLoading(plan);
      setError(null);
      setSuccessMessage(null);

      // Razorpay checkout flow for all paid plans
      const response = await createPaymentOrder(plan);
        
        const options = {
          key: response.key_id,
          amount: response.amount,
          currency: response.currency,
          name: "LeadGen AI",
          description: "Upgrade to " + plan.charAt(0).toUpperCase() + plan.slice(1) + " Plan",
          order_id: response.order_id,
          theme: { color: "#3B82F6" },
          handler: async (razorpayResponse) => {
            try {
              await verifyPayment({
                razorpay_order_id: razorpayResponse.razorpay_order_id,
                razorpay_payment_id: razorpayResponse.razorpay_payment_id,
                razorpay_signature: razorpayResponse.razorpay_signature,
                plan: plan
              });
              
              const planName = plan.charAt(0).toUpperCase() + plan.slice(1);
              setSuccessMessage(`Payment successful! Upgraded to ${planName} Plan`);
              
              await fetchPlan();
              
              setTimeout(() => {
                window.location.reload();
              }, 2000);
            } catch (err) {
              console.error("Payment verification failed:", err);
              setError("Payment verification failed. Contact support.");
              setActionLoading(null);
            }
          },
          modal: {
            ondismiss: () => {
              setActionLoading(null);
            }
          },
          prefill: {
            email: ""
          }
        };
        
        const rzp = new window.Razorpay(options);
        rzp.open();
    } catch (err) {
      setError("Failed to create payment order. Please try again.");
      console.error("Error in handleUpgrade:", err);
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20 text-gray-400">
        Loading pricing details...
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-12 animate-fade-in bg-zinc-950 min-h-screen">
      <Helmet>
        <title>Pricing — LeadGen AI | AI Lead Generation Plans from ₹399/month</title>
        <meta name="description" content="Choose your LeadGen AI plan. Search Google, YouTube, LinkedIn & 8+ platforms. AI prospect scoring, email finder, social media extraction. Start with a free 2-day trial." />
        <link rel="canonical" href="https://leadgenai.in/pricing" />        
      </Helmet>
      <div className="text-center mb-16">
        <button 
          onClick={() => navigate('/search')}
          className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-4 text-sm inline-flex"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
            <path d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back
        </button>
        <h1 className="text-4xl font-bold text-white mb-4">Choose Your Plan</h1>
        <p className="text-xl text-gray-400">Upgrade your plan to unlock more leads and features</p>
      </div>

      {error && (
        <div className="mb-8 p-4 rounded-xl bg-red-500/10 border border-red-500/50 text-red-400 text-center">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="mb-8 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/50 text-emerald-400 text-center">
          {successMessage}
        </div>
      )}

      {isTrial && (
        <div className="mb-8 p-6 rounded-2xl bg-gradient-to-r from-purple-900/50 to-pink-900/50 border border-purple-500/50 max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <span className="text-2xl">🚀</span>
                <h2 className="text-xl font-bold text-white">You're on FREE Premium Trial!</h2>
              </div>
              <div className="mb-1">
                {trialRemainingHours > 24 ? (
                  <span className="text-white font-medium">{Math.floor(trialRemainingHours / 24)} days remaining</span>
                ) : trialRemainingHours > 0 ? (
                  <span className="text-yellow-400 font-medium">{trialRemainingHours} hours remaining</span>
                ) : (
                  <span className="text-red-400 font-bold">Trial expired!</span>
                )}
              </div>
              <p className="text-gray-400 text-sm">Enjoy all Premium features free for 2 days</p>
            </div>
            <button
              onClick={() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })}
              className="px-6 py-3 bg-white text-dark-950 font-bold rounded-xl hover:bg-slate-200 transition-colors shrink-0"
            >
              Choose a Plan
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
        {/* BASIC PLAN */}
        <div className={`relative p-8 rounded-2xl border ${currentPlan === 'basic' ? 'border-blue-500 bg-zinc-800' : 'border-white/10 bg-zinc-900'} flex flex-col transition-transform hover:-translate-y-1`}>
          {currentPlan === 'basic' && (
            <span className="absolute -top-4 left-1/2 -translate-x-1/2 bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
              Current Plan
            </span>
          )}
          {currentPlan === 'trial' && (
            <span className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-3 py-1 rounded-full text-sm font-semibold whitespace-nowrap">
              🎉 Free Trial Active
            </span>
          )}
          <div className="mb-8">
            <h3 className="text-2xl font-semibold text-white mb-2">Basic</h3>
            <div className="flex items-baseline text-white">
              <span className="text-4xl font-bold">₹399</span>
              <span className="text-gray-400 ml-2">/ month</span>
            </div>
            <p className="text-gray-400 mt-4 h-12">Perfect for getting started. Search any platform with AI-powered chat.</p>
          </div>
          
          <ul className="space-y-3 mb-8 flex-1 text-sm">
            <li className="flex items-center gap-2.5 text-gray-300"><svg className="w-4 h-4 text-green-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>50 leads per day</li>
            <li className="flex items-center gap-2.5 text-gray-300"><svg className="w-4 h-4 text-green-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>AI chat search</li>
            <li className="flex items-center gap-2.5 text-gray-300"><svg className="w-4 h-4 text-green-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>AI lead scoring</li>
            <li className="flex items-center gap-2.5 text-gray-300"><svg className="w-4 h-4 text-green-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>CSV export</li>
            <li className="flex items-center gap-2.5 text-gray-300"><svg className="w-4 h-4 text-green-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>82 countries</li>
          </ul>

          <button
            onClick={() => handleUpgrade('basic')}
            disabled={currentPlan === 'basic' || actionLoading !== null}
            className={`w-full py-3 rounded-xl font-medium transition-colors ${actionLoading !== null ? 'opacity-70 cursor-not-allowed' : ''} ${
              currentPlan === 'basic' 
                ? 'bg-zinc-700 text-gray-500 cursor-not-allowed opacity-60' 
                : 'bg-zinc-700 hover:bg-zinc-600 text-gray-400 border border-white/10'
            }`}
          >
            {actionLoading === 'basic' ? (
              <>
                <svg className="animate-spin h-5 w-5 mr-2 inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                Processing...
              </>
            ) : (
              currentPlan === 'basic' ? 'Current Plan' : currentPlan === 'trial' ? 'Buy Basic — ₹399/mo' : (currentPlan && currentPlan !== 'none' ? 'Downgrade to Basic' : 'Buy Basic — ₹399/mo')
            )}
          </button>
        </div>

        {/* STANDARD PLAN */}
        <div className={`relative p-8 rounded-2xl border ${currentPlan === 'standard' ? 'border-blue-400 bg-blue-900/10' : 'border-white/10 bg-zinc-900'} flex flex-col transition-transform hover:-translate-y-1`}>
          {currentPlan === 'standard' && (
            <span className="absolute -top-4 left-1/2 -translate-x-1/2 bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
              Current Plan
            </span>
          )}
          <div className="mb-8">
            <h3 className="text-2xl font-semibold text-blue-400 mb-2">Standard</h3>
            <div className="flex items-baseline text-white">
              <span className="text-4xl font-bold">₹999</span>
              <span className="text-gray-400 ml-2">/ month</span>
            </div>
            <p className="text-gray-400 mt-4 h-12">For growing businesses. Unlock multi-category search and Excel exports.</p>
          </div>
          
          <ul className="space-y-3 mb-8 flex-1 text-sm">
            <li className="flex items-center gap-2.5 text-gray-300"><svg className="w-4 h-4 text-blue-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>500 leads per day</li>
            <li className="flex items-center gap-2.5 text-gray-300"><svg className="w-4 h-4 text-blue-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>CSV & Excel export</li>
            <li className="flex items-center gap-2.5 text-gray-300"><svg className="w-4 h-4 text-blue-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>Multi-category search</li>
            <li className="flex items-center gap-2.5 text-gray-300"><svg className="w-4 h-4 text-blue-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>AI lead scoring</li>
            <li className="flex items-center gap-2.5 text-gray-300"><svg className="w-4 h-4 text-blue-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>Priority support</li>
          </ul>

          <button
            onClick={() => handleUpgrade('standard')}
            disabled={currentPlan === 'standard' || actionLoading !== null}
            className={`w-full py-3 rounded-xl font-medium transition-all ${actionLoading !== null ? 'opacity-70 cursor-not-allowed' : ''} ${
              currentPlan === 'standard' 
                ? 'bg-zinc-700 text-gray-500 cursor-not-allowed opacity-60' 
                : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white'
            }`}
          >
            {actionLoading === 'standard' ? (
              <>
                <svg className="animate-spin h-5 w-5 mr-2 inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                Processing...
              </>
            ) : (
              currentPlan === 'standard' ? 'Current Plan' : currentPlan === 'trial' ? 'Upgrade to Standard — ₹999/mo' : currentPlan === 'premium' ? 'Downgrade to Standard' : 'Upgrade to Standard'
            )}
          </button>
        </div>

        {/* PREMIUM PLAN */}
        <div className={`relative p-8 rounded-2xl bg-gradient-to-b from-zinc-800 to-zinc-900 flex flex-col transition-transform hover:-translate-y-1 transform scale-105 z-10`}
             style={{ 
               boxShadow: currentPlan === 'premium' ? '0 0 30px rgba(168, 85, 247, 0.4)' : '0 0 20px rgba(168, 85, 247, 0.2)',
               border: '1px solid rgba(168, 85, 247, 0.5)'
             }}>
          <div className="absolute -top-4 inset-x-0 flex justify-center space-x-2">
            <span className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-3 py-1 rounded-full text-sm font-semibold shadow-lg">
              Popular
            </span>
            {currentPlan === 'premium' && (
              <span className="bg-white text-purple-600 px-3 py-1 rounded-full text-sm font-semibold shadow-lg">
                Current
              </span>
            )}
            {currentPlan === 'trial' && (
              <span className="bg-white text-purple-600 px-3 py-1 rounded-full text-sm font-semibold shadow-lg">
                Recommended
              </span>
            )}
          </div>
          
          <div className="mb-8">
            <h3 className="text-2xl font-semibold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400 mb-2">Premium</h3>
            <div className="flex items-baseline text-white">
              <span className="text-4xl font-bold">₹2499</span>
              <span className="text-gray-400 ml-2">/ month</span>
            </div>
            <p className="text-gray-400 mt-4 h-12">Full power unleashed. AI prospect analysis, email finder, and unlimited everything.</p>
          </div>
          
          <ul className="space-y-3 mb-8 flex-1 text-sm">
            <li className="flex items-center gap-2.5 text-gray-300"><svg className="w-4 h-4 text-purple-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>1000 leads per day</li>
            <li className="flex items-center gap-2.5 text-gray-300"><svg className="w-4 h-4 text-purple-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>CSV & Excel export</li>
            <li className="flex items-center gap-2.5 text-gray-300"><svg className="w-4 h-4 text-purple-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>Email finder included</li>
            <li className="flex items-center gap-2.5 text-gray-300"><svg className="w-4 h-4 text-purple-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>Deep AI analysis</li>
            <li className="flex items-center gap-2.5 text-gray-300"><svg className="w-4 h-4 text-purple-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>Freelancer mode</li>
            <li className="flex items-center gap-2.5 text-gray-300"><svg className="w-4 h-4 text-purple-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>Priority support</li>
          </ul>

          <button
            onClick={() => handleUpgrade('premium')}
            disabled={currentPlan === 'premium' || actionLoading !== null}
            className={`w-full py-3 rounded-xl font-bold transition-all shadow-lg ${actionLoading !== null ? 'opacity-70 cursor-not-allowed' : ''} ${
              currentPlan === 'premium' 
                ? 'bg-zinc-700 text-gray-500 cursor-not-allowed opacity-60' 
                : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white'
            }`}
          >
            {actionLoading === 'premium' ? (
              <>
                <svg className="animate-spin h-5 w-5 mr-2 inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                Processing...
              </>
            ) : (
              currentPlan === 'premium' ? 'Current Plan' : currentPlan === 'trial' ? 'Upgrade to Premium — ₹2499/mo' : 'Upgrade to Premium'
            )}
          </button>
        </div>

      </div>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* COMPARE ALL FEATURES TABLE */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <div className="max-w-6xl mx-auto mt-20 mb-16">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-white">📊 Compare All Features</h2>
          <p className="mt-3 text-gray-400">See exactly what each plan includes</p>
        </div>

        <div className="overflow-x-auto rounded-2xl border border-white/10">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-zinc-800">
                <th className="text-left px-4 py-3 text-gray-400 font-medium min-w-[200px]">Feature</th>
                <th className="text-center px-4 py-3 text-white font-semibold min-w-[100px]">Basic</th>
                <th className="text-center px-4 py-3 text-blue-400 font-semibold min-w-[100px]">Standard</th>
                <th className="text-center px-4 py-3 text-purple-400 font-semibold min-w-[100px] bg-purple-500/5">Premium</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['Leads per day', '50', '500', '1000 (Unlimited)'],
                ['Searches per day', '5', '25', 'Unlimited'],
                ['Google Places Search', true, true, true],
                ['YouTube Channel Search', true, true, true],
                ['Platform Search (LinkedIn etc.)', true, true, true],
                ['AI Chat Search', true, true, true],
                ['AI Lead Scoring', true, true, true],
                ['Multi-Country (82)', true, true, true],
                ['Chat History', true, true, true],
                ['CSV Export', true, true, true],
                ['Excel Export', false, true, true],
                ['Multi-Category Search', false, true, true],
                ['Multi-City Search', false, true, true],
                ['Auto Email Finder', false, false, true],
                ['Phone Extraction', false, false, true],
                ['Social Media Links (8 platforms)', false, false, true],
                ['AI Prospect Analysis', false, false, true],
                ['AI Approach Suggestions', false, false, true],
                ['Competitor Auto-Exclusion', false, false, true],
                ['Freelancer Mode', false, false, true],
                ['Priority Support', false, false, true],
              ].map(([feature, basic, standard, premium], idx) => (
                <tr key={idx} className={idx % 2 === 0 ? 'bg-zinc-900' : 'bg-zinc-900/60'}>
                  <td className="px-4 py-2.5 text-gray-300">{feature}</td>
                  <td className="px-4 py-2.5 text-center">
                    {typeof basic === 'boolean'
                      ? basic ? <svg className="w-4 h-4 text-green-400 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg> : <span className="text-gray-600 text-sm">—</span>
                      : <span className="text-white font-medium">{basic}</span>
                    }
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    {typeof standard === 'boolean'
                      ? standard ? <svg className="w-4 h-4 text-green-400 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg> : <span className="text-gray-600 text-sm">—</span>
                      : <span className="text-white font-medium">{standard}</span>
                    }
                  </td>
                  <td className="px-4 py-2.5 text-center bg-purple-500/5">
                    {typeof premium === 'boolean'
                      ? premium ? <svg className="w-4 h-4 text-green-400 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg> : <span className="text-gray-600 text-sm">—</span>
                      : <span className="text-white font-medium">{premium}</span>
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* FAQ */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <div className="max-w-3xl mx-auto mb-16">
        <h2 className="text-2xl font-bold text-white text-center mb-8">Frequently Asked Questions</h2>
        <div className="space-y-4">
          {[
            {
              q: 'What happens after my free trial ends?',
              a: "After your 2-day free trial, you'll be moved to the Basic plan. You can upgrade anytime to unlock more features and higher limits."
            },
            {
              q: 'Can I upgrade or downgrade anytime?',
              a: 'Yes! You can switch plans at any time. Upgrades take effect immediately. Downgrades apply at the end of your current billing period.'
            },
            {
              q: 'What payment methods do you accept?',
              a: 'We accept all major payment methods through Razorpay — UPI, credit/debit cards, net banking, wallets, and more.'
            },
            {
              q: 'Is there a refund policy?',
              a: "We offer a full refund within 7 days of purchase if you're not satisfied. No questions asked."
            }
          ].map((faq, idx) => (
            <div key={idx} className="bg-zinc-900 rounded-2xl p-5 border border-white/10">
              <h3 className="font-medium text-white mb-2">{faq.q}</h3>
              <p className="text-gray-400 text-sm leading-relaxed">{faq.a}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
